import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import { Calendar, Clock, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (s) => {
  if (!s) return "";
  try {
    const d = new Date(s);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return s;
  }
};

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/blogs`)
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div data-testid="blog-page">
      <PageHero
        eyebrow="Blog"
        title={<>Health stories from <span className="italic text-brand-maroon">our doctors.</span></>}
        subtitle="Practical guides, seasonal advice and patient education — written by the doctors and team at Family HealthCare Hospital."
      />

      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-20">
        {loading ? (
          <p className="text-brand-ink/60">Loading posts…</p>
        ) : posts.length === 0 ? (
          <p className="text-brand-ink/60">No posts yet. Check back soon.</p>
        ) : (
          <>
            {/* Featured */}
            {featured && (
              <Link
                to={`/blog/${featured.slug}`}
                data-testid={`blog-featured-${featured.slug}`}
                className="group grid md:grid-cols-12 gap-6 items-stretch rounded-3xl overflow-hidden bg-white border border-brand-beige hover:shadow-lg transition mb-12"
              >
                <div className="md:col-span-7 aspect-[16/10] md:aspect-auto overflow-hidden">
                  <img src={featured.cover_image} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="md:col-span-5 p-8 md:p-12 flex flex-col justify-center">
                  <div className="text-xs uppercase tracking-[0.22em] font-bold text-brand-maroon">{featured.category}</div>
                  <h2 className="font-serif-display text-3xl md:text-4xl text-brand-ink mt-3 leading-[1.15]">{featured.title}</h2>
                  <p className="mt-4 text-brand-ink/65 leading-relaxed">{featured.excerpt}</p>
                  <div className="mt-6 flex items-center gap-4 text-xs text-brand-ink/55">
                    <span className="inline-flex items-center gap-1.5"><Calendar size={13} /> {fmtDate(featured.published_at)}</span>
                    <span className="inline-flex items-center gap-1.5"><Clock size={13} /> {featured.read_time}</span>
                  </div>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-maroon">
                    Read article <ArrowUpRight size={16} />
                  </span>
                </div>
              </Link>
            )}

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group rounded-3xl overflow-hidden bg-white border border-brand-beige hover:shadow-lg transition"
                >
                  <div className="aspect-[5/3] overflow-hidden">
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-brand-maroon">{p.category}</div>
                    <h3 className="font-serif-display text-xl text-brand-ink mt-2 leading-[1.2]">{p.title}</h3>
                    <p className="text-sm text-brand-ink/65 mt-2 line-clamp-2">{p.excerpt}</p>
                    <div className="mt-5 flex items-center gap-4 text-xs text-brand-ink/55">
                      <span className="inline-flex items-center gap-1.5"><Calendar size={12} /> {fmtDate(p.published_at)}</span>
                      <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {p.read_time}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
